// utils/videoEncoderUtils.js - Utilities for WebCodecs Video Encoding

/**
 * Check browser support for WebCodecs
 * @returns {Object} Support status and details
 */
export const checkWebCodecsSupport = () => {
  const support = {
    isSupported: false,
    hasVideoEncoder: false,
    hasVideoFrame: false,
    hasMediaStreamTrack: false,
    browser: getBrowserInfo(),
    message: '',
  };

  // Check for required APIs
  support.hasVideoEncoder = typeof VideoEncoder !== 'undefined';
  support.hasVideoFrame = typeof VideoFrame !== 'undefined';
  support.hasMediaStreamTrack = typeof MediaStreamTrackProcessor !== 'undefined';

  support.isSupported = 
    support.hasVideoEncoder && 
    support.hasVideoFrame;

  // Generate user-friendly message
  if (support.isSupported) {
    support.message = '✅ Your browser supports WebCodecs video encoding';
  } else if (support.browser.name === 'Safari') {
    support.message = '⚠️ Safari has limited WebCodecs support. Please use Chrome or Edge.';
  } else if (support.browser.name === 'Firefox') {
    support.message = '⚠️ Firefox does not yet support WebCodecs. Please use Chrome or Edge.';
  } else {
    support.message = '❌ Your browser does not support WebCodecs. Please update to the latest Chrome or Edge.';
  }

  return support;
};

/**
 * Get browser information
 * @returns {Object} Browser name and version
 */
export const getBrowserInfo = () => {
  const ua = navigator.userAgent;
  let browserName = 'Unknown';
  let version = 'Unknown';

  if (ua.includes('Firefox')) {
    browserName = 'Firefox';
    version = ua.match(/Firefox\/(\d+)/)?.[1] || 'Unknown';
  } else if (ua.includes('Edg/')) {
    browserName = 'Edge';
    version = ua.match(/Edg\/(\d+)/)?.[1] || 'Unknown';
  } else if (ua.includes('Chrome')) {
    browserName = 'Chrome';
    version = ua.match(/Chrome\/(\d+)/)?.[1] || 'Unknown';
  } else if (ua.includes('Safari') && !ua.includes('Chrome')) {
    browserName = 'Safari';
    version = ua.match(/Version\/(\d+)/)?.[1] || 'Unknown';
  }

  return { name: browserName, version };
};

/**
 * Estimate video file size
 * @param {number} duration - Video duration in seconds
 * @param {number} bitrate - Video bitrate in bits per second
 * @returns {Object} Size estimates
 */
export const estimateVideoSize = (duration, bitrate = 5_000_000) => {
  const bitsTotal = duration * bitrate;
  const bytesTotal = bitsTotal / 8;
  const megabytes = bytesTotal / (1024 * 1024);

  return {
    bytes: Math.round(bytesTotal),
    megabytes: megabytes.toFixed(2),
    formatted: formatFileSize(bytesTotal),
  };
};

/**
 * Format file size to human-readable string
 * @param {number} bytes 
 * @returns {string}
 */
export const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
  if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
};

/**
 * Calculate optimal encoding settings based on slide count
 * @param {number} slideCount 
 * @param {number} durationPerSlide 
 * @returns {Object} Encoding settings
 */
export const getOptimalEncodingSettings = (slideCount, durationPerSlide = 3) => {
  const totalDuration = slideCount * durationPerSlide;
  
  // Adjust settings based on video length
  let fps = 30;
  let bitrate = 5_000_000; // 5 Mbps

  if (totalDuration > 300) { // > 5 minutes
    fps = 24;
    bitrate = 4_000_000; // 4 Mbps
  } else if (totalDuration > 600) { // > 10 minutes
    fps = 24;
    bitrate = 3_000_000; // 3 Mbps
  }

  return {
    fps,
    bitrate,
    bitrateFormatted: `${(bitrate / 1_000_000).toFixed(1)} Mbps`,
    estimatedSize: estimateVideoSize(totalDuration, bitrate),
  };
};

/**
 * Validate encoding options
 * @param {Object} options 
 * @returns {Object} Validation result
 */
export const validateEncodingOptions = (options) => {
  const errors = [];
  const warnings = [];

  // Check slides
  if (!options.slides || options.slides.length === 0) {
    errors.push('No slides provided');
  }

  // Check duration
  if (options.duration && (options.duration < 0.5 || options.duration > 30)) {
    warnings.push('Duration should be between 0.5 and 30 seconds per slide');
  }

  // Check FPS
  if (options.fps && (options.fps < 15 || options.fps > 60)) {
    warnings.push('FPS should be between 15 and 60');
  }

  // Check audio file size (if provided)
  if (options.audioFile && options.audioFile.size > 100 * 1024 * 1024) {
    warnings.push('Audio file is very large (>100MB). Encoding may be slow.');
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
};

/**
 * Create timestamp for filename
 * @returns {string}
 */
export const createTimestamp = () => {
  return new Date()
    .toISOString()
    .slice(0, 19)
    .replace(/[:.]/g, '-');
};

/**
 * Generate video filename
 * @param {string} prefix 
 * @returns {string}
 */
export const generateVideoFilename = (prefix = 'presentation') => {
  const timestamp = createTimestamp();
  return `${prefix}-${timestamp}.mp4`;
};

/**
 * Log encoding stats
 * @param {Object} stats 
 */
export const logEncodingStats = (stats) => {
 

};

/**
 * Performance tracker for encoding
 */
export class EncodingPerformanceTracker {
  constructor() {
    this.startTime = null;
    this.endTime = null;
    this.framesTimes = [];
  }

  start() {
    this.startTime = performance.now();
  }

  recordFrame() {
    if (this.startTime) {
      this.framesTimes.push(performance.now() - this.startTime);
    }
  }

  end() {
    this.endTime = performance.now();
  }

  getStats() {
    if (!this.startTime || !this.endTime) {
      return null;
    }

    const totalTime = (this.endTime - this.startTime) / 1000; // seconds
    const framesEncoded = this.framesTimes.length;
    const avgFps = framesEncoded / totalTime;

    return {
      totalTime,
      framesEncoded,
      avgFps: avgFps.toFixed(2),
      formatted: `${totalTime.toFixed(2)}s (${avgFps.toFixed(1)} fps avg)`,
    };
  }
}

/**
 * Error handler for WebCodecs
 * @param {Error} error 
 * @returns {string} User-friendly error message
 */
export const handleEncodingError = (error) => {


  if (error.name === 'NotSupportedError') {
    return 'Your browser does not support this video codec. Please try a different browser.';
  }

  if (error.name === 'QuotaExceededError') {
    return 'Not enough memory to encode video. Try reducing the number of slides or using a simpler template.';
  }

  if (error.message?.includes('codec')) {
    return 'Video codec configuration failed. Please try again or use the server-based export.';
  }

  if (error.message?.includes('audio')) {
    return 'Audio processing failed. Try exporting without audio or use the server-based export.';
  }

  return `Video encoding failed: ${error.message || 'Unknown error'}. Please try the server-based export.`;
};

export default {
  checkWebCodecsSupport,
  getBrowserInfo,
  estimateVideoSize,
  formatFileSize,
  getOptimalEncodingSettings,
  validateEncodingOptions,
  createTimestamp,
  generateVideoFilename,
  logEncodingStats,
  EncodingPerformanceTracker,
  handleEncodingError,
};
